import { useState } from "react"
import { trpc } from "~/lib/trpc"
import { useAuth } from "~/lib/auth-context"
import { Button } from "@aix/ui"
import { useNavigate } from "@tanstack/react-router"
import { LayoutGrid, Plus, Loader2, ExternalLink, Pencil, Trash2 } from "lucide-react"
import toast from "react-hot-toast"
import { useOpenTabs } from "./hooks/useOpenTabs"
import { PackFormModal } from "./PackFormModal"
import type { EditablePack } from "./PackFormModal"

export function LinkPacksScreen() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { openAll } = useOpenTabs()
  const [editing, setEditing] = useState<EditablePack | null>(null)
  const [showCreate, setShowCreate] = useState(false)

  const { data, isLoading, refetch } = trpc.linkPacks.list.useQuery()

  const deleteMutation = trpc.linkPacks.delete.useMutation({
    onSuccess: () => {
      toast.success("Pack deleted")
      void refetch()
    },
    onError: (error) => toast.error(error.message || "Failed to delete pack"),
  })

  const handleOpenAll = (e: React.MouseEvent, links: { url: string }[]) => {
    e.stopPropagation()
    const blocked = openAll(links.map((l) => l.url))
    if (blocked.length > 0) {
      toast.error(`${blocked.length} tab(s) blocked. Open the pack page to allow pop-ups and retry.`)
    }
  }

  const handleDelete = (e: React.MouseEvent, pack: EditablePack) => {
    e.stopPropagation()
    if (window.confirm(`Delete "${pack.name}"? This can't be undone.`)) {
      deleteMutation.mutate({ id: pack.id })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  const packs = data?.packs ?? []

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
            <LayoutGrid className="w-7 h-7" />
            LinkPacks
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Open a whole set of links as browser tabs in one click. Share any pack with a link.
          </p>
        </div>
        {user && (
          <Button onClick={() => setShowCreate(true)} className="w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            New Pack
          </Button>
        )}
      </div>

      {packs.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-lg">
          <LayoutGrid className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-1">No tab packs yet</h3>
          <p className="text-sm text-muted-foreground mb-6">
            {user ? "Create your first pack to get started." : "Sign in to create a pack."}
          </p>
          {user && (
            <Button onClick={() => setShowCreate(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Pack
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {packs.map((pack) => (
            <div
              key={pack.id}
              className="group flex flex-col p-5 border border-border rounded-lg cursor-pointer hover:bg-secondary/50 hover:shadow-md transition-all duration-200"
              onClick={() => navigate({ to: "/linkpacks/$slug", params: { slug: pack.slug } })}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="text-3xl leading-none flex-shrink-0">{pack.emoji || "🗂️"}</div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-lg text-foreground truncate group-hover:text-primary transition-colors">
                    {pack.name}
                  </h3>
                  <p className="text-xs text-muted-foreground">{pack.links.length} tabs</p>
                </div>
                {user && (
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setEditing(pack)
                      }}
                      className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted"
                      aria-label="Edit pack"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => handleDelete(e, pack)}
                      className="p-1.5 text-muted-foreground hover:text-destructive rounded-md hover:bg-muted"
                      aria-label="Delete pack"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {pack.description && (
                <p className="text-sm text-muted-foreground line-clamp-2 mb-4 flex-1">{pack.description}</p>
              )}

              <Button onClick={(e) => handleOpenAll(e, pack.links)} className="w-full mt-auto" variant="outline">
                <ExternalLink className="w-4 h-4 mr-2" />
                Open all {pack.links.length} tabs
              </Button>
            </div>
          ))}
        </div>
      )}

      {showCreate && <PackFormModal onClose={() => setShowCreate(false)} onSaved={() => setShowCreate(false)} />}
      {editing && <PackFormModal pack={editing} onClose={() => setEditing(null)} onSaved={() => setEditing(null)} />}
    </div>
  )
}
